import { z } from 'zod';
import { api } from '@/lib/api';
import {
  JobDetailResponseSchema,
  JobRecommendationsResponseSchema,
  JobsListResponseSchema,
  JobsPageResponseSchema,
  type AnswerJobInputBody,
  type CreateJobBody,
  type JobDetail,
  type JobRecommendationsResponse,
  type JobsListResponse,
  type JobsPageResponse,
  type PostJobMessageBody,
  type ResumeJobBody,
  type UpdateJobBody,
} from './schemas';

function tenantHeaders(tenantId: string): Record<string, string> {
  return { 'X-Tenant-Id': tenantId };
}

export async function listJobs(tenantId: string): Promise<JobsListResponse> {
  return api.get('/tenant/jobs', JobsListResponseSchema, { headers: tenantHeaders(tenantId) });
}

export async function getJob(tenantId: string, id: string): Promise<JobDetail> {
  const r = await api.get(`/tenant/jobs/${encodeURIComponent(id)}`, JobDetailResponseSchema, {
    headers: tenantHeaders(tenantId),
  });
  return r.item;
}

export async function createJob(tenantId: string, body: CreateJobBody): Promise<JobDetail> {
  const r = await api.post('/tenant/jobs', JobDetailResponseSchema, body, {
    headers: tenantHeaders(tenantId),
  });
  return r.item;
}

export async function getJobRecommendations(
  tenantId: string,
  templateId: string,
): Promise<JobRecommendationsResponse> {
  return api.get(
    `/tenant/jobs/recommendations?template_id=${encodeURIComponent(templateId)}`,
    JobRecommendationsResponseSchema,
    { headers: tenantHeaders(tenantId) },
  );
}

export async function updateJob(
  tenantId: string,
  id: string,
  body: UpdateJobBody,
): Promise<JobDetail> {
  const r = await api.patch(
    `/tenant/jobs/${encodeURIComponent(id)}`,
    JobDetailResponseSchema,
    body,
    { headers: tenantHeaders(tenantId) },
  );
  return r.item;
}

// ─── Jobs page (Yours + Suggested) ─────────────────────────────────────
export async function getJobsPage(tenantId: string): Promise<JobsPageResponse> {
  return api.get('/tenant/jobs-page', JobsPageResponseSchema, { headers: tenantHeaders(tenantId) });
}

const VoidSchema = z.void();

export async function favoriteTemplate(tenantId: string, templateId: string): Promise<void> {
  await api.post(
    `/tenant/jobs-page/favorites/${encodeURIComponent(templateId)}`,
    VoidSchema,
    {},
    { headers: tenantHeaders(tenantId) },
  );
}

export async function unfavoriteTemplate(tenantId: string, templateId: string): Promise<void> {
  await api.delete(`/tenant/jobs-page/favorites/${encodeURIComponent(templateId)}`, VoidSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function dismissSuggestedTemplate(
  tenantId: string,
  templateId: string,
): Promise<void> {
  await api.post(
    `/tenant/jobs-page/dismissals/${encodeURIComponent(templateId)}`,
    VoidSchema,
    {},
    { headers: tenantHeaders(tenantId) },
  );
}

export async function undismissAllSuggestions(tenantId: string): Promise<void> {
  await api.delete('/tenant/jobs-page/dismissals', VoidSchema, {
    headers: tenantHeaders(tenantId),
  });
}

export async function hideTemplateFromJobs(tenantId: string, templateId: string): Promise<void> {
  await api.post(
    `/tenant/jobs-page/visibility/${encodeURIComponent(templateId)}/hide`,
    VoidSchema,
    {},
    { headers: tenantHeaders(tenantId) },
  );
}

export async function deleteJob(tenantId: string, id: string): Promise<void> {
  await api.delete(`/tenant/jobs/${encodeURIComponent(id)}`, z.void(), {
    headers: tenantHeaders(tenantId),
  });
}

export async function cancelJob(tenantId: string, id: string): Promise<JobDetail> {
  const r = await api.post(
    `/tenant/jobs/${encodeURIComponent(id)}/cancel`,
    JobDetailResponseSchema,
    {},
    {
      headers: tenantHeaders(tenantId),
    },
  );
  return r.item;
}

// ─── Job Detail lifecycle ──────────────────────────────────────────────

export async function pauseJob(tenantId: string, id: string): Promise<JobDetail> {
  const r = await api.post(
    `/tenant/jobs/${encodeURIComponent(id)}/pause`,
    JobDetailResponseSchema,
    {},
    { headers: tenantHeaders(tenantId) },
  );
  return r.item;
}

export async function resumeJob(
  tenantId: string,
  id: string,
  body: ResumeJobBody = {},
): Promise<JobDetail> {
  const r = await api.post(
    `/tenant/jobs/${encodeURIComponent(id)}/resume`,
    JobDetailResponseSchema,
    body,
    { headers: tenantHeaders(tenantId) },
  );
  return r.item;
}

export async function postJobMessage(
  tenantId: string,
  id: string,
  body: PostJobMessageBody,
): Promise<JobDetail> {
  const r = await api.post(
    `/tenant/jobs/${encodeURIComponent(id)}/messages`,
    JobDetailResponseSchema,
    body,
    { headers: tenantHeaders(tenantId) },
  );
  return r.item;
}

export async function answerJobInput(
  tenantId: string,
  id: string,
  body: AnswerJobInputBody,
): Promise<JobDetail> {
  const r = await api.post(
    `/tenant/jobs/${encodeURIComponent(id)}/answer-input`,
    JobDetailResponseSchema,
    body,
    { headers: tenantHeaders(tenantId) },
  );
  return r.item;
}

export async function acknowledgeJob(tenantId: string, id: string): Promise<JobDetail> {
  const r = await api.post(
    `/tenant/jobs/${encodeURIComponent(id)}/acknowledge`,
    JobDetailResponseSchema,
    {},
    { headers: tenantHeaders(tenantId) },
  );
  return r.item;
}

export async function setJobLastSeen(
  tenantId: string,
  id: string,
  turnNumber: number,
): Promise<void> {
  await api.post(
    `/tenant/jobs/${encodeURIComponent(id)}/last-seen`,
    VoidSchema,
    { turn_number: turnNumber },
    { headers: tenantHeaders(tenantId) },
  );
}

const SaveAsTemplateResponseSchema = z.object({ item: z.object({ id: z.string() }) });

export async function saveJobAsTemplate(
  tenantId: string,
  jobId: string,
  body: { title: string },
): Promise<{ item: { id: string } }> {
  return api.post(
    `/tenant/jobs/${encodeURIComponent(jobId)}/save-as-template`,
    SaveAsTemplateResponseSchema,
    body,
    { headers: tenantHeaders(tenantId) },
  );
}
